import { useState, useEffect, useCallback, useRef } from 'react';
import { sowService } from '../services/sowService';

/**
 * Custom hook for managing async SOW generation jobs
 * Handles polling, progress tracking, and state management
 */
export const useAsyncJob = () => {
  const [jobState, setJobState] = useState({
    jobId: null,
    status: 'idle', // idle, polling, completed, failed
    progress: 0,
    message: '',
    result: null,
    error: null,
    estimatedCompletion: null,
    startTime: null,
    elapsedTime: 0,
    retryCount: 0
  });

  const pollingRef = useRef(null);
  const intervalRef = useRef(null);

  // Stop polling
  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Start polling for job status
  const startPolling = useCallback((jobId) => {
    if (pollingRef.current) {
      return; // Already polling
    }

    console.log(`🔍 Starting polling for job: ${jobId}`);

    const pollJob = async () => {
      try {
        const status = await sowService.getJobStatus(jobId);
        
        setJobState(prev => ({
          ...prev,
          status: status.status === 'completed' ? 'completed' : 
                  status.status === 'failed' ? 'failed' : 'polling',
          progress: status.progress || prev.progress,
          message: status.message || prev.message,
          result: status.status === 'completed' ? status : null,
          error: status.status === 'failed' ? status.message : null,
          elapsedTime: Date.now() - prev.startTime
        }));

        if (status.status === 'completed') {
          console.log('✅ Job completed successfully');
          stopPolling();
        } else if (status.status === 'failed') {
          console.error('❌ Job failed:', status.message);
          stopPolling();
        }
      } catch (error) {
        console.error('Error polling job status:', error);
        
        // Handle specific error cases
        if (error.message.includes('not found')) {
          setJobState(prev => ({
            ...prev,
            status: 'failed',
            error: 'Job not found - it may have been processed by the legacy system',
            message: 'Job not found. The document may have been generated using the legacy synchronous system.'
          }));
          stopPolling();
        } else if (error.message.includes('async processing system')) {
          setJobState(prev => ({
            ...prev,
            status: 'failed',
            error: 'Async system not ready',
            message: 'The new async processing system is not fully deployed yet. Please try the legacy system or contact support.'
          }));
          stopPolling();
        } else {
          // For other errors, continue polling for a few more attempts
          const maxRetries = 5;
          
          setJobState(prev => {
            const currentRetry = prev.retryCount || 0;
            
            if (currentRetry >= maxRetries) {
              stopPolling();
              return {
                ...prev,
                status: 'failed',
                error: error.message,
                message: `Polling failed after ${maxRetries} retries: ${error.message}`
              };
            } else {
              return {
                ...prev,
                retryCount: currentRetry + 1,
                message: `Polling error (retry ${currentRetry + 1}/${maxRetries}): ${error.message}`
              };
            }
          });
        }
      }
    };

    // Poll immediately, then every 2 seconds
    pollJob();
    pollingRef.current = setInterval(pollJob, 2000);

    // Update elapsed time every second
    intervalRef.current = setInterval(() => {
      setJobState(prev => ({
        ...prev,
        elapsedTime: Date.now() - prev.startTime
      }));
    }, 1000);
  }, [stopPolling]);

  // Start a new async job
  const startJob = useCallback(async (sowRequest) => {
    try {
      console.log('🚀 Starting async SOW generation job');
      
      setJobState({
        jobId: null,
        status: 'submitting',
        progress: 0,
        message: 'Submitting SOW generation request...',
        result: null,
        error: null,
        estimatedCompletion: null,
        startTime: Date.now(),
        elapsedTime: 0,
        retryCount: 0
      });

      const response = await sowService.generateSOW(sowRequest);

      if (response.async) {
        // Async job started - begin polling
        setJobState(prev => ({
          ...prev,
          jobId: response.job_id,
          status: 'polling',
          message: response.message || 'SOW generation queued successfully',
          estimatedCompletion: response.estimated_completion
        }));

        // Start polling for job completion
        startPolling(response.job_id);
        
        return { success: true, jobId: response.job_id, async: true };
      } else {
        // Sync completion (legacy)
        setJobState(prev => ({
          ...prev,
          jobId: response.document_id,
          status: 'completed',
          progress: 100,
          message: 'SOW generated successfully!',
          result: response
        }));
        
        return { success: true, result: response, async: false };
      }
    } catch (error) {
      console.error('Error starting SOW job:', error);
      setJobState(prev => ({
        ...prev,
        status: 'failed',
        error: error.message,
        message: `Failed to start SOW generation: ${error.message}`
      }));
      throw error;
    }
  }, [startPolling]);

  // Reset job state
  const resetJob = useCallback(() => {
    stopPolling();
    setJobState({
      jobId: null,
      status: 'idle',
      progress: 0,
      message: '',
      result: null,
      error: null,
      estimatedCompletion: null,
      startTime: null,
      elapsedTime: 0,
      retryCount: 0
    });
  }, [stopPolling]);

  // Download completed document
  const downloadDocument = useCallback(async () => {
    if (!jobState.result || jobState.status !== 'completed') {
      throw new Error('No completed document available for download');
    }

    try {
      if (jobState.result.download_url) {
        // Use presigned URL
        return await sowService.downloadFromPresignedUrl(
          jobState.result.download_url,
          jobState.result.filename
        );
      } else if (jobState.result.s3_url) {
        // Use S3 URL
        return await sowService.downloadFromPresignedUrl(
          jobState.result.s3_url,
          jobState.result.filename
        );
      } else if (jobState.result.document_id) {
        // Use legacy download method
        return await sowService.downloadDocument(jobState.result.document_id);
      } else {
        throw new Error('No download URL available');
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      throw error;
    }
  }, [jobState.result, jobState.status]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  // Calculate progress percentage for display
  const getProgressPercentage = useCallback(() => {
    if (jobState.status === 'completed') return 100;
    if (jobState.status === 'failed') return 0;
    if (jobState.status === 'idle') return 0;
    if (jobState.status === 'submitting') return 5;
    
    // For polling status, use reported progress or estimate based on time
    if (jobState.progress > 0) {
      return Math.min(jobState.progress, 95); // Cap at 95% until completion
    }
    
    // Estimate progress based on elapsed time (rough estimate)
    const elapsedMinutes = jobState.elapsedTime / (1000 * 60);
    if (elapsedMinutes < 1) return 20;
    if (elapsedMinutes < 2) return 50;
    if (elapsedMinutes < 3) return 80;
    return 90; // Cap at 90% for long-running jobs
  }, [jobState.status, jobState.progress, jobState.elapsedTime]);

  // Format elapsed time for display
  const getFormattedElapsedTime = useCallback(() => {
    if (!jobState.startTime) return '';
    
    const seconds = Math.floor(jobState.elapsedTime / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${remainingSeconds}s`;
  }, [jobState.elapsedTime, jobState.startTime]);

  return {
    // State
    jobState,
    isIdle: jobState.status === 'idle',
    isSubmitting: jobState.status === 'submitting',
    isPolling: jobState.status === 'polling',
    isCompleted: jobState.status === 'completed',
    isFailed: jobState.status === 'failed',
    isActive: ['submitting', 'polling'].includes(jobState.status),
    
    // Computed values
    progressPercentage: getProgressPercentage(),
    formattedElapsedTime: getFormattedElapsedTime(),
    
    // Actions
    startJob,
    resetJob,
    downloadDocument,
    stopPolling
  };
};

export default useAsyncJob;